import type { Key } from "react-stately";

import { useMatchRoute, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";

import { usePageTitle } from "@/hooks/usePageTitle";
import { useUser } from "@/hooks/useUser";

import {
  BILLING_TAB_CONFIG,
  BILLING_TABS,
  billingTabLabel,
} from "../config/billing-tab-routes";
import { BillingShell } from "./BillingShell";

interface BillingPageProps {
  readonly projectId: string;
}

export function BillingPage({ projectId }: BillingPageProps) {
  const { navigate } = useRouter();
  const matchRoute = useMatchRoute();
  const { data: userData } = useUser();

  const getCurrentTab = (): string => {
    if (matchRoute({ to: "/billing/$projectId/overview" }))
      return BILLING_TABS.OVERVIEW;
    if (matchRoute({ to: "/billing/$projectId/invoices" }))
      return BILLING_TABS.INVOICES;
    if (matchRoute({ to: "/billing/$projectId/payment-methods" }))
      return BILLING_TABS.PAYMENT_METHODS;
    if (matchRoute({ to: "/billing/$projectId/history" }))
      return BILLING_TABS.HISTORY;

    return BILLING_TABS.OVERVIEW;
  };

  const selectedTab = getCurrentTab();
  const tabLabel = billingTabLabel(selectedTab as never) ?? "Overview";

  const project = userData?.projects.find((p) => p.id === Number(projectId));
  const accountName = project?.name ?? "";

  usePageTitle(`Billing - ${tabLabel}`);

  const handleTabChange = useCallback(
    (key: Key) => {
      navigate({
        params: { projectId },
        to: `/billing/$projectId/${key.toString()}`,
      });
    },
    [projectId, navigate],
  );

  return (
    <BillingShell
      accountName={accountName}
      onTabChange={handleTabChange}
      selectedTab={selectedTab}
      tabs={BILLING_TAB_CONFIG}
    />
  );
}
